import { _MiscMessages, ConfigSystemParams, DashboardProfit } from 'src/app/core/consts';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { catchError, debounceTime, distinctUntilChanged, EMPTY, map, merge, Observable, of, Subject, switchMap, takeUntil } from 'rxjs';
import { ConfigSystemService } from 'src/app/core/services/utelize/system/config-system/config-system-service';
import { DashboardService } from 'src/app/core/services/utelize/general/dashboard-service';
import { DashboardStatistic, DashboardStatistic_EmptyObject } from 'src/app/core/interfaces/general/dashboard-statistic';
import { DashboardTotal_Previous_Current_PercentDTO_EmptyObject } from 'src/app/core/models/system/dashboard/dashboard-total-previous-current-percent.dto';
import { ExceptionsService } from 'src/app/core/services/utelize/general/exceptions-service';
import { OurDate } from 'src/app/core/interfaces/general/our-date';
import { populateOurDate } from 'src/app/core/functions-generic/functions-generic-date';
import { TippyToolTipService } from 'src/app/core/services/utelize/general/tippy-tool-tip-service';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-widget-row-usage',
  templateUrl: './widget-row-usage.component.html',
  styleUrls: ['./widget-row-usage.component.scss'],
  providers: [TippyToolTipService]
})

export class WidgetRowUsageComponent implements AfterViewInit, OnDestroy, OnInit {
  
  @Input() containerTitle: string | undefined;
  @Input() useContainer  : boolean | undefined;
    
  get pollingWidgets    () { return this.configSystemService.getValueAsBoolean(ConfigSystemParams.WidgetRow_Polling_Usage,           false); }
  get showtotalCompanies() { return this.configSystemService.getValueAsBoolean(ConfigSystemParams.DashboardShowWidgetTotalCompanies, false); }
  get tbDisplayEnabled  () { return this.configSystemService.getValueAsBoolean(ConfigSystemParams.DashboardUsageDisplayTbEnabled,    true ); }

  get maxWidgetsPerRow() { return this.configSystemService.getValueAsInteger(ConfigSystemParams.WidgetRow_MaxWidgetsPerRow_Usage, 4); }

  private filterCompanyID         : number | undefined;
  private filterDate              : OurDate | undefined=populateOurDate(this.exceptionsService, Date.now());
  private statTitleTotalCompanies : string="Total companies";
  private statTitleTotalCostsUsage: string="Usage costs";
  private statTitleTotalSims      : string="Total live SIMs";
  private statTitleTotalUsage     : string="Total usage";

  private destroy$  =new Subject<void>();
  private selection$=new Subject<{ companyId: number; date: { year: number; month: number; day: number } }>();

  private readonly USAGE_TB_THRESHOLD_GB: number=1024;

  public expandContainer: boolean=true;
  public statData!      : Array<DashboardStatistic>;

  constructor(
    private changeDetectorRef  : ChangeDetectorRef,
    private configSystemService: ConfigSystemService,
    private dashboardService   : DashboardService,
    private exceptionsService  : ExceptionsService,
    private tippyToolTipService: TippyToolTipService
  ) {

  }

  public ngOnInit(): void {
    try {
      this.initSelectionStream();
      
      if (!this.containerTitle || this.containerTitle.trim()=="")
        this.containerTitle=_MiscMessages.TitleNotSet;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  public ngAfterViewInit(): void {
    try {
      setTimeout(() => {
        this.displayData();
      });
      
      this.tippyToolTipService.setupBootstrapTooltips('[data-bs-toggle="tooltip"]', 'left');
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  public ngOnDestroy(): void {
    try {
      this.destroy$.next();
      this.destroy$.complete();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  private displayData() {
    try {
      this.statData=[];

      let statTotalCompanies: DashboardStatistic=DashboardStatistic_EmptyObject();
      
      if (this.showtotalCompanies) {
        statTotalCompanies={
          decimalPlaces : 0,
          icon          : "bxs-business",
          iconColour    : "success",
          link          : "/companies/companies",
          linkText      : "View Company Details",
          loading       : true,
          percentage    : <string><unknown>"",
          profit        : "",
          profitUpIsGood: true,
          showProfit    : true,
          showSpinner   : false,
          title         : this.statTitleTotalCompanies,
          value         : undefined,
        }
      }

      let statTotalCostsUsage: DashboardStatistic={
        decimalPlaces : 2,
        icon          : "bx-pound",
        iconColour    : "success",
        link          : "/provisioning/devices-allocated",
        linkText      : "View SIM Costs",
        loading       : true,
        percentage    : <string><unknown>"",
        prefix        : "£",
        profit        : "",
        profitUpIsGood: true,
        showProfit    : true,
        showSpinner   : false,
        subTitle      : "Not inc licences",
        title         : this.statTitleTotalCostsUsage,
        value         : undefined,
      }
            
      let statTotalSims: DashboardStatistic={
        decimalPlaces : 0,
        icon          : "bi bi-sim",
        iconColour    : "info",
        link          : "/provisioning/devices-allocated",
        linkText      : "View SIMs",
        loading       : true,
        percentage    : <string><unknown>"",
        profit        : "",
        profitUpIsGood: true,
        showProfit    : true,
        showSpinner   : false,
        title         : this.statTitleTotalSims,
        value         : undefined,
      }
            
      let statTotalUsage: DashboardStatistic={
        decimalPlaces : 2,
        icon          : "bx-line-chart",
        iconColour    : "warning",
        link          : "/provisioning/devices-allocated",
        linkText      : "View SIM Usage",
        loading       : true,
        percentage    : <string><unknown>"",
        profit        : "",
        profitUpIsGood: true,
        showProfit    : true,
        showSpinner   : false,
        subTitle      : "During month",
        title         : this.statTitleTotalUsage,
        unit          : "GB",
        value         : undefined,
      }
                
      this.statData.push(statTotalSims);
      this.statData.push(statTotalUsage);

      if (this.showtotalCompanies)
        this.statData.push(statTotalCompanies);
      
      this.statData.push(statTotalCostsUsage);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
       return (undefined);
    }
  }
  
  private initSelectionStream(): void {
    try {
      this.selection$
      .pipe(
        debounceTime(150),
        distinctUntilChanged((a, b) =>
          a.companyId ===b.companyId  &&
          a.date.year ===b.date.year  &&
          a.date.month===b.date.month &&
          a.date.day  ===b.date.day
        ),
        switchMap(selected => {
          const companyId=selected.companyId;
          const year     =selected.date.year;
          const month    =selected.date.month;
          const day      =selected.date.day;

          this.resetTilesToLoading();

          const safeTile=<T>(observable: Observable<T>, applyFn: (data: T) => void) =>
            observable.pipe(
              catchError(err => {
                this.exceptionsService.handleException(new Error(this.exceptionsService.formatHttpError(err)));
                return EMPTY;
              }),
              map(data => { applyFn(data); })
            );

          const totalCompaniesSource=this.showtotalCompanies
                                     ? (
                                        this.pollingWidgets
                                        ? this.dashboardService.getTotalCompaniesCountUpdates(companyId, year, month, day)
                                        : this.dashboardService.getTotalCompaniesCount       (companyId, year, month, day)
                                       )
                                     : of(DashboardTotal_Previous_Current_PercentDTO_EmptyObject());

          const totalCostsUsageSource=this.pollingWidgets
                                      ? this.dashboardService.getTotalCostsUsageUpdates(companyId, year, month, day)
                                      : this.dashboardService.getTotalCostsUsage       (companyId, year, month, day);

          const totalSimsSource=this.pollingWidgets
                                ? this.dashboardService.getTotalSimCountUpdates(companyId, year, month, day)
                                : this.dashboardService.getTotalSimCount       (companyId, year, month, day);

          const totalUsageSource=this.pollingWidgets
                                 ? this.dashboardService.getTotalUsageUpdates(companyId, year, month, day)
                                 : this.dashboardService.getTotalUsage       (companyId, year, month, day);

          return merge(
            safeTile(totalCompaniesSource,  d => { if (this.showtotalCompanies) this.applyTotalCompanies(d); }),
            safeTile(totalCostsUsageSource, d => this.applyTotalCostsUsage(d)),
            safeTile(totalSimsSource,       d => this.applyTotalSimCount  (d)),
            safeTile(totalUsageSource,      d => this.applyTotalUsage     (d))
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.changeDetectorRef.markForCheck();
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private resetTilesToLoading(): void {
    try {
      if (!this.statData)
        return;

      this.statData.forEach((stat: DashboardStatistic) => {
        stat.loading=true;
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
    
  public emitSelection(filterCompanyID: number | undefined, filterDate: OurDate | undefined): void {
    try {
      this.filterCompanyID=filterCompanyID;
      this.filterDate     =filterDate;

      const companyId=this.filterCompanyID ?? -1;
      const date     ={
        year : <number>(this.filterDate?.year  ?? 1),
        month: <number>(this.filterDate?.month ?? 1),
        day  : <number>(this.filterDate?.day   ?? 1),
      };
      
      this.selection$.next({ companyId, date });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  private applyTotalCostsUsage(data: { percentageChange: number; currentTotal: number }) {
    try {
      if (!this.statData)
        this.displayData();
  
      this.statData.forEach((stat: DashboardStatistic) => {
        if (stat.title==this.statTitleTotalCostsUsage) {
          stat.loading   =false,
          stat.percentage=<string><unknown>data.percentageChange,
          stat.profit    =data.percentageChange==0 ? DashboardProfit.Equal : (data.percentageChange<0 ? DashboardProfit.Down : DashboardProfit.Up),
          stat.value     =data.currentTotal
        }
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  private applyTotalCompanies(data: { percentageChange: number; currentTotal: number }) {
    try {
      if (!this.showtotalCompanies)
        return;
      
      if (!this.statData)
        this.displayData();
  
      this.statData.forEach((stat: DashboardStatistic) => {
        if (stat.title==this.statTitleTotalCompanies) {
          stat.loading   =false,
          stat.percentage=<string><unknown>data.percentageChange,
          stat.profit    =data.percentageChange==0 ? DashboardProfit.Equal : (data.percentageChange<0 ? DashboardProfit.Down : DashboardProfit.Up),
          stat.value     =data.currentTotal
        }
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  private applyTotalSimCount(data: { percentageChange: number; currentTotal: number }) {
    try {
      if (!this.statData)
        this.displayData();
  
      this.statData.forEach((stat: DashboardStatistic) => {
        if (stat.title==this.statTitleTotalSims) {
          stat.loading   =false,
          stat.percentage=<string><unknown>data.percentageChange,
          stat.profit    =data.percentageChange==0 ? DashboardProfit.Equal : (data.percentageChange<0 ? DashboardProfit.Down : DashboardProfit.Up),
          stat.value     =data.currentTotal
        }
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  private applyTotalUsage(data: { percentageChange: number; currentTotal: number }) {
    try {
      if (!this.statData)
        this.displayData();
  
      this.statData.forEach((stat: DashboardStatistic) => {
        if (stat.title==this.statTitleTotalUsage) {
          stat.loading   =false,
          stat.percentage=<string><unknown>data.percentageChange,
          stat.profit    =data.percentageChange==0 ? DashboardProfit.Equal : (data.percentageChange<0 ? DashboardProfit.Down : DashboardProfit.Up),
          stat.value     =data.currentTotal
        }
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  public trendPercent(stat: DashboardStatistic): number | null {
    try {
      if (!stat?.showProfit || stat?.percentage==null || stat.percentage=="")
        return null;

      const percent=Number(stat.percentage);

      return Number.isFinite(percent) ? percent : null;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return null;
    }
  }

  private showUsageAsTb(stat: DashboardStatistic): boolean {
    try {
      if (stat?.unit!=='GB' || typeof stat.value!=='number' || !this.tbDisplayEnabled)
        return false;

      return stat.value>=this.USAGE_TB_THRESHOLD_GB;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return false;
    }
  }

  public usageSuffix(stat: DashboardStatistic): string | undefined {
    try {
      return this.showUsageAsTb(stat) ? 'TB' : stat?.unit;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return stat?.unit;
    }
  }

  public usageValue(stat: DashboardStatistic): string | number | null {
    try {
      if (this.showUsageAsTb(stat))
        return <number>stat.value/this.USAGE_TB_THRESHOLD_GB;

      return stat?.value ?? null;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return stat?.value ?? null;
    }
  }

  public toggleContainerExpansion(): void {
    try {
      this.expandContainer=!this.expandContainer;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  public collapseContainer(): void {
    try {
      this.expandContainer=false;
      this.changeDetectorRef.markForCheck();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
}
